import React, { useMemo } from "react";
import { styled, Typography, SxProps, Stack, Chip, Card, Link, Tooltip, Fade, IconButton } from "@mui/material";
import PosterImage from "../../Misc/PosterImage";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { DbMarkerSeverity } from "../../../Core/Api/Types/db-marker-severity";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { GetFrameRate, ReactPlayerMarker } from "../../../Redux/Reducers/redux-slice-react-player";
import { FrameToTimecode, JWPMarkerTimecode } from "../../../Utils/Timecode/ReactPlayer";
import StatusMarkerRender from "../../../Utils/Marker/marker";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import { assetService, cacheService } from "../../../Core/Api/services";
import { DbAssetMarkerInfos } from "../../../Core/Api/Types/db-asset-marker-infos";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import ModalDuplicateMarker, {
  ModalDuplicateMarkerProps,
} from "../../Modals/ModalDuplicateMaker/modal-duplicate-marker";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { CopyAllOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../Redux/redux-store";

export type QCMarkerCardProps = {
  className?: string;
  id: number;
  title?: string | null;
  subtitle?: string | null;
  desc: string | null;
  thumbnail?: string;
  timecodes: { in: ReactPlayerMarker; out: ReactPlayerMarker; frameRate: number };
  tag: DbMarkerCategory | null;
  status: DbMarkerSeverity | null;
  contentOnly?: boolean;
  deleteCallback?: () => void;
};

export const MapQCMarkerData = (markers: DbAssetMarkerInfos[], frameDuration: number): QCMarkerCardProps[] => {
  return markers.map<QCMarkerCardProps>((marker) => {
    return {
      id: marker.id,
      title: marker.familyInfos?.name,
      subtitle: marker.typeInfos?.name,
      desc: marker.comment,
      tag: marker.category,
      status: marker.severity,
      thumbnail: marker.thumbnailIn !== null ? "data:image/jpeg;base64," + marker.thumbnailIn?.$value! : undefined,
      timecodes: {
        in: {
          markFrame: marker.frameIn === null ? 0 : marker.frameIn,
          markTimecode:
            marker?.timecodeIn || FrameToTimecode(marker.frameIn == null ? 0 : marker.frameIn, marker.frameRate!),
          markTimecodeFile: marker.timecodeInFile!,
          isSet: true,
        },
        out: {
          markFrame: marker.frameOut === null ? frameDuration : marker.frameOut,
          markTimecode:
            marker?.timecodeOut ||
            FrameToTimecode(marker.frameOut === null ? frameDuration : marker.frameOut, marker.frameRate!),
          markTimecodeFile: marker.timecodeOutFile!,
          isSet: marker?.frameOut !== null,
        },
        frameRate: marker.frameRate!,
      },
    };
  });
};

const QCMarkerCard: React.FC<QCMarkerCardProps> = (props) => {
  const {
    id,
    title,
    subtitle,
    desc,
    timecodes,
    tag,
    status,
    thumbnail,
    contentOnly = false,
    deleteCallback,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  const customSnackbar = useCustomSnackbar();
  const [hover, setHover] = React.useState(false);
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  //Cache
  const platformMode = cacheService.GetPlatformMode();
  const isDrive = useMemo(() => platformMode === PlatformMode.Browser, [platformMode]);

  const HandleDeleteMarkerConfirm = (id: number) => {
    if (deleteCallback === undefined) return;
    deleteCallback();
    assetService.DeleteMarkerByMarkerId(id).then(() => {
      customSnackbar(t("alert:success.markerHasBeenDeleted", { title }), "success");
    });
  };

  const frameRate = useAppSelector(GetFrameRate);

  const _renderContent = () =>
    isDrive ? (
      <>
        <Stack gap={1}>
          <Stack direction="row" gap={1}>
            <Stack direction="column" gap={1} flexGrow={1}>
              <Typography variant="body1" color="text.primary" flexGrow={1}>
                {desc === null ? `${title} / ${subtitle}` : desc}
              </Typography>
              <Stack direction="row" gap={1} flexWrap={"wrap"}>
                <JWPMarkerTimecode frameRate={frameRate} {...timecodes.in}></JWPMarkerTimecode>
                <JWPMarkerTimecode frameRate={frameRate} {...timecodes.out}></JWPMarkerTimecode>
              </Stack>
            </Stack>
            {thumbnail !== undefined && (
              <PreviewMarker img={{ src: thumbnail! }} sx={{ width: "33%", aspectRatio: "16 / 9" }} flexShrink={0} />
            )}
          </Stack>
        </Stack>
      </>
    ) : (
      <>
        <Stack gap={1}>
          <Stack gap={1}>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Stack>
                <Typography fontWeight={500}>{title}</Typography>
                <Typography color="text.tier" variant="body2">
                  {subtitle}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="top" spacing={0.5}>
                <Chip label={tag} size="small" />
                <StatusMarkerRender status={status!}></StatusMarkerRender>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <JWPMarkerTimecode frameRate={frameRate} {...timecodes.in}></JWPMarkerTimecode>
              <JWPMarkerTimecode frameRate={frameRate} {...timecodes.out}></JWPMarkerTimecode>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems={"flex-start"} gap={2}>
            <Typography variant="body2" color="text.tier" mt={1} flexGrow={1}>
              {desc}
            </Typography>
            {thumbnail !== undefined && (
              <PreviewMarker img={{ src: thumbnail! }} sx={{ width: "33%", aspectRatio: "16 / 9" }} flexShrink={0} />
            )}
          </Stack>
        </Stack>
      </>
    );

  return contentOnly ? (
    _renderContent()
  ) : (
    <StyledCard
      variant="outlined"
      onMouseEnter={(e) => setHover(true)}
      onMouseLeave={(e) => setHover(false)}
      {...otherProps}
    >
      {_renderContent()}
      <Fade in={hover}>
        <Stack
          direction="row"
          gap={0}
          sx={(t) => ({
            background: t.helpers(t).maskingGradient.backgroundDefault,
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 2,
            p: 1.5,
            pl: 3,
          })}
        >
          {!isDrive && (
            <IconButton
              color="ghost"
              size="small"
              onClick={() => {
                if (!openModal) {
                  return;
                }
                openModal<ModalDuplicateMarkerProps>(ModalDuplicateMarker, {
                  markerid: id,
                  onConfirm: () => {},
                });
              }}
            >
              <CopyAllOutlined />
            </IconButton>
          )}
          {deleteCallback && (
            <IconButton
              color="error"
              size="small"
              onClick={() => {
                if (!openModal) return;

                openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                  displayComment: false,
                  commentLabel: t("modal:comment.confirmDeleteMarker"),
                  title: t("modal:title.deleteThisMarker"),
                  onConfirm: () => HandleDeleteMarkerConfirm(id),
                });
              }}
            >
              <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
            </IconButton>
          )}
        </Stack>
      </Fade>
    </StyledCard>
  );
};

export const StyledCard = styled(Card)(({ theme, onClick }) => ({
  padding: theme.spacing(1.5),
  position: "relative",
  "&:active": {
    transform: onClick && "scale(0.95)",
    opacity: onClick && 0.95,
  },
  "&:hover": {
    ".marker-actions": {},
  },
}));

export const PreviewMarker = styled(PosterImage)(({ theme, img }) => ({
  position: "relative",
  "&:before": {
    content: '""',
    position: "absolute",
    // left: 0,
    // right: 0,
    // bottom: 0,
    // top: 0,
    background: "", //img.src === "" ? "none" : "radial-gradient(86.28% 385.52% at 70.39% 33.17%, rgba(0, 0, 0, 0) 0%, #000000 100%)",
  },
}));

export default QCMarkerCard;
